import * as React from "react";
import styled from "styled-components";

import i18n from "../../lib/i18n";
import { Company, Post } from "../entities";
import { DraftPostPin } from "../reducers/posts";
import { media } from "../../common/utils/style";
import Translate from "../../common/components/Translate";
import Carousel from "../../common/components/Carousel";
import { NavigatableSection, SectionInner, SectionTitle, SectionLink } from "./Section";
import { navFullPath } from "./HeaderNav";
import StartActionCard from "./StartActionCard";
import EditableCardWrapper from "./EditableCardWrapper";
import FeaturedPostCard from "../../posts/components/FeaturedPostCard";
import { PostCardSimplePlaceholder } from "../../posts/components/PostCardSimple";

interface Props {
  company: Company;
  posts: Post[];
  draftPostPins: DraftPostPin[];
  locale: string;
  showAllPostsLink?: boolean;
  editable?: boolean;
  onEdit?: (index: number) => void;
}

const PostsSection: React.SFC<Props> = props => {
  const { company, locale, posts, draftPostPins, showAllPostsLink, editable, onEdit } = props;
  const hasContent = posts.length > 0;

  if (!editable && !hasContent) {
    return null;
  }
  return (
    <NavigatableSection id="posts" displayName={i18n.translate("company.home.sections.posts", { locale })}>
      <SectionInner>
        <SectionTitle>
          <Translate id="company.home.sections.posts" />
          {showAllPostsLink && (
            <SectionLink to={navFullPath(company, "/feed")}>
              <Translate id="company.home.view_other_posts" />
            </SectionLink>
          )}
        </SectionTitle>
      </SectionInner>
      {editable &&
        !hasContent && (
          <StartActionCard
            message={i18n.translate("company.home.start_feed_card.message", { locale })}
            subMessage={i18n.translate("company.home.start_feed_card.sub_message", { locale })}
            buttonText={i18n.translate("company.home.start_feed_card.button_text", { locale })}
            linkPath={`/manage_posts/articles/new?company_id=${company.id}`}
          />
        )}
      {hasContent && (
        <FeaturedPostWrapper>
          <CarouselWrapper>
            <Carousel
              layouts={{
                small: { perPage: 1, gutter: 16 },
                medium: { perPage: 2, gutter: 16 },
                large: { perPage: 3, gutter: 30 },
              }}
              childCount={editable ? 3 : posts.length}
              renderChild={index => {
                const post = posts[index];
                const pin = draftPostPins.find(p => p.category_cd === index);
                if (post != null) {
                  return (
                    <EditableCardWrapper
                      editable={editable}
                      onClick={() => {
                        if (onEdit) {
                          onEdit(index);
                        }
                      }}
                    >
                      <FeaturedPostCard post={post} pin={pin} editable={editable} />
                    </EditableCardWrapper>
                  );
                }
                return <PostCardSimplePlaceholder linkPath={`/manage_posts/articles/new?company_id=${company.id}`} />;
              }}
            />
          </CarouselWrapper>
        </FeaturedPostWrapper>
      )}
    </NavigatableSection>
  );
};

PostsSection.defaultProps = {
  editable: false,
};

const FeaturedPostWrapper = styled.div`
  overflow-x: hidden;
`;

const CarouselWrapper = styled(SectionInner)`
  margin: 0 30px 0 16px;
  @media ${media.large} {
    margin: 0 auto;
  }
`;

export default PostsSection;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/FeaturedPostsSection.tsx